import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useTranslate } from '../components/TranslationText';
import { useSelector } from 'react-redux';
import axios from '../axios/axiosUmbraco';
import Album from '../components/Album';
import Lightbox from 'react-18-image-lightbox';
import NetaqInput from '../components/NetaqInput';
import NewActionButton from '../components/ActionButtons/NewActionButton';
import SearchBox from '../components/SearchBox';
import { formatDateTimeToString } from '../helper/formatDateTimeToString';
import useUmbracoQuery from '../hooks/useUmbracoQuery';
import {
    checkIsReturning,
    consumeReturnTicket
} from '../helper/pageFilterPersistence';

const GalleryId = process.env.REACT_APP_GALLERY_ID;
const MediaBaseURL = process.env.REACT_APP_UMBRACO_MEDIA_BASE_URL;

const GALLERY_TICKET_KEY = "gallery_return_from_details";
const GALLERY_SEARCH_KEY = "gallery_searchKeyWord";
const GALLERY_SEASON_KEY = "gallery_selectedSeason";

function clearGallerySessionStorage() {
    sessionStorage.removeItem(GALLERY_SEARCH_KEY);
    sessionStorage.removeItem(GALLERY_SEASON_KEY);
    sessionStorage.removeItem(GALLERY_TICKET_KEY);
}

export default function GalleryPage() {
    const { translate } = useTranslate();
    const { currentLanguage } = useSelector((state) => state.studBookSlice);

    const albumsPerPage = 3;

    const navigationType = useNavigationType();
    const location = useLocation();
    const prevLocationKey = useRef(location.key);
    const isInitialMount = useRef(true);
    const wasReturningRef = useRef(false);

    if (isInitialMount.current) {
        isInitialMount.current = false;
        const isReturning = checkIsReturning(GALLERY_TICKET_KEY, navigationType, "gallery");
        wasReturningRef.current = isReturning;
        if (!isReturning) {
            clearGallerySessionStorage();
        }
    }

    function getCurrentLanguageUmbraco() {
        const lang = localStorage.getItem("eahsLang") ?? "en";
        return lang === "en" ? "en-US" : "ar-AE";
    }

    const [seasonList, setSeasonList] = useState([]);
    const [searchKeyWord, setSearchKeyWord] = useState(() => {
        return sessionStorage.getItem(GALLERY_SEARCH_KEY) || "";
    });
    const [selectedSeason, setSellectedSeason] = useState(() => {
        const saved = sessionStorage.getItem(GALLERY_SEASON_KEY);
        return saved ? JSON.parse(saved) : {
            label: translate('web.season', 'Season'),
            value: null,
        };
    });

    useEffect(() => {
        sessionStorage.setItem(GALLERY_SEARCH_KEY, searchKeyWord);
    }, [searchKeyWord]);

    useEffect(() => {
        sessionStorage.setItem(GALLERY_SEASON_KEY, JSON.stringify(selectedSeason));
    }, [selectedSeason]);

    useEffect(() => {
        if (wasReturningRef.current) {
            consumeReturnTicket(GALLERY_TICKET_KEY);
        }
    }, []);

    useEffect(() => {
        if (prevLocationKey.current !== location.key) {
            prevLocationKey.current = location.key;
            if (navigationType === "PUSH") {
                clearGallerySessionStorage();
                setSearchKeyWord("");
                setSellectedSeason({
                    label: translate('web.season', 'Season'),
                    value: null,
                });
            }
        }
    }, [location.key, navigationType, translate]);
    
    // Setup API filters
    const apiFilters = useMemo(() => {
        return {
            season: selectedSeason?.value || '',
            albumName: searchKeyWord.trim() || '',
        };
    }, [selectedSeason, searchKeyWord]);

    const queryFetchId = GalleryId?.startsWith('children:') ? GalleryId : `children:${GalleryId}`;

    const {
        data: galleryRaw,
        isLoading,
        hasMore,
        fetchNextPage
    } = useUmbracoQuery(queryFetchId, apiFilters, albumsPerPage, true, '');

    // Fetch dynamic category list (Option B)
    useEffect(() => {
        async function fetchSeasons() {
            try {
                const url = `/umbraco/delivery/api/v1/content?fetch=${queryFetchId}&skip=0&take=2000&fields=properties[season]`;
                const umbraco = await axios.get(url, {
                    headers: {
                        "accept-language": getCurrentLanguageUmbraco(),
                    },
                });

                const allSeasons = umbraco.data.items.map(i => i.properties?.season).filter(Boolean);
                // Add default season if empty and not in the list (as per original logic)
                const finalSeasons = allSeasons.length > 0 ? allSeasons : ["2024-2025"];
                
                const uniqueFormattedData = [...new Set(finalSeasons)]
                    .map(season => ({ label: season, value: season, text: season }));
                setSeasonList(uniqueFormattedData);
            } catch (e) {
                console.error("Failed to fetch season categories", e);
            }
        }
        fetchSeasons();
    }, [currentLanguage, queryFetchId]);

    const formattedData = useMemo(() => {
        return galleryRaw.map((item) => ({
            albumName: item?.properties?.albumName?.toLowerCase() ?? "",
            albumMedia: item?.properties?.albumCover ?? [],
            albumId: item?.id ?? '',
            albumStartDate: formatDateTimeToString(item?.properties?.eventStartDate) ?? '',
            albumEndDate: formatDateTimeToString(item?.properties?.eventEndDate) ?? "",
            eventId: item?.properties?.eventId ?? "",
            season: item?.properties?.season ? item?.properties?.season : "2024-2025"
        }));
    }, [galleryRaw]);

    // light Box
    const [imageslightbox, setImageslightbox] = useState([]);
    const [index, setIndex] = useState(-1);

    const currentImage = imageslightbox[index];

    const handleClick = (index, albumMedia) => {
        setIndex(index);
        setImageslightbox(albumMedia.map((media) => MediaBaseURL + media.url));
    };
    const handleClose = () => setIndex(-1);

    return (
        <>
            <div className='row rtl'>
                <h2 className='text-start fw-bold col-lg-6 col-md-6  col-sm-12 col-12 mb-main' >
                    {translate('web.gallery', 'Gallery')}
                </h2>
                <div className='col-lg-3 col-md-3 col-sm-12 mb-main' >
                    <NetaqInput type={'select'} isClearable={true} name={'season'} placeholder={translate('web.season', 'Season')} label={translate('web.season', 'Season')} listSelect={seasonList} value={selectedSeason} hideLable={true} onChange={(e) => setSellectedSeason(e)} />
                </div>
                <div className='col-lg-3 col-md-3 col-sm-12 mb-main ' >
                    <SearchBox
                        onSubmit={(e) => {
                            e.preventDefault();
                        }}
                        isFullWidth
                        onChange={(e) => setSearchKeyWord(e.target.value)}
                        placeholder={translate('web.search', 'Search').toUpperCase()}
                        isForm={true}
                        value={searchKeyWord}
                        key={'searchBox'}
                    />
                </div>
            </div>
            {isLoading && formattedData.length === 0 && (
                <div className="text-center w-100 mt-5">
                    <i className="fas fa-spinner fa-spin fa-3x color-eahsred"></i>
                </div>
            )}

            {formattedData.map((item, index) => (
                <Album handleClick={handleClick} item={item} index={index} key={index} />
            ))}
            
            {!isLoading && formattedData.length === 0 && (
                 <div className='text-center bold color-eahsred mt-5 w-100'>
                    {translate('web.nodataFound', 'No Albums Found with Your Filter Parameters')}
                 </div>
            )}
            
            {hasMore && formattedData.length > 0 && (
                <div className="row m0 rtl">
                    <div className='col-xl-2 col-lg-3 col-md-3 col-sm-4 col-12 px-0 mx-auto'>
                        {isLoading ? (
                            <div className="text-center w-100 py-3">
                                <i className="fas fa-spinner fa-spin fa-2x color-eahsred"></i>
                            </div>
                        ) : (
                            <NewActionButton
                                isSubmitBtn
                                isRed
                                isNoMargin
                                text={translate('web.loadMore', 'Load More')}
                                onClick={fetchNextPage}
                            />
                        )}
                    </div>
                </div>
            )}

            {!!currentImage && (
                <Lightbox
                    wrapperClassName='ltr'
                    mainSrc={imageslightbox[index]}
                    nextSrc={imageslightbox[(index + 1) % imageslightbox.length]}
                    prevSrc={imageslightbox[(index + imageslightbox.length - 1) % imageslightbox.length]}
                    onCloseRequest={handleClose}
                    onMovePrevRequest={() =>
                        setIndex((index + imageslightbox.length - 1) % imageslightbox.length)
                    }
                    onMoveNextRequest={() =>
                        setIndex((index + 1) % imageslightbox.length)
                    }
                />
            )}
        </>
    );
}

